import { AlertCircle, CheckCircle2, MapPin } from 'lucide-react';
import type { AddressVerificationResponse, StandardizedAddress } from '@/api/address';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface AddressVerificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  verificationResult: AddressVerificationResponse | null;
  onAcceptSuggested: (address: StandardizedAddress) => void;
  onKeepOriginal: () => void;
}

function formatAddress(address: {
  streetAddress: string;
  secondaryAddress?: string | null;
  city: string;
  state: string;
  zipCode: string;
}): string {
  const parts = [address.streetAddress];
  if (address.secondaryAddress) {
    parts.push(address.secondaryAddress);
  }
  parts.push(`${address.city}, ${address.state} ${address.zipCode}`);
  return parts.join('\n');
}

function AddressDisplay({
  label,
  address,
  variant = 'default',
}: {
  label: string;
  address: {
    streetAddress: string;
    secondaryAddress?: string | null;
    city: string;
    state: string;
    zipCode: string;
  };
  variant?: 'default' | 'suggested' | 'original';
}) {
  const bgClass =
    variant === 'suggested'
      ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'
      : variant === 'original'
        ? 'bg-muted/50 border-muted'
        : 'bg-muted/50 border-muted';

  return (
    <div className={`rounded-lg border p-4 ${bgClass}`}>
      <div className="flex items-start gap-3">
        <MapPin
          className={`h-5 w-5 mt-0.5 ${variant === 'suggested' ? 'text-green-600' : 'text-muted-foreground'}`}
        />
        <div className="flex-1">
          <p
            className={`font-medium text-sm mb-1 ${variant === 'suggested' ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'}`}
          >
            {label}
          </p>
          <p className="whitespace-pre-line text-sm">{formatAddress(address)}</p>
        </div>
      </div>
    </div>
  );
}

export function AddressVerificationDialog({
  open,
  onOpenChange,
  verificationResult,
  onAcceptSuggested,
  onKeepOriginal,
}: AddressVerificationDialogProps) {
  if (!verificationResult) {
    return null;
  }

  const { verified, hasCorrections, suggestedAddress, originalAddress, error } = verificationResult;

  // If verified and no corrections, just close (address is good)
  if (verified && !hasCorrections) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Address Verified
            </DialogTitle>
            <DialogDescription>
              Your address has been verified by USPS and is confirmed as deliverable.
            </DialogDescription>
          </DialogHeader>

          <AddressDisplay label="Verified Address" address={originalAddress} variant="suggested" />

          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // If there's an error
  if (error && !suggestedAddress) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Address Verification
            </DialogTitle>
            <DialogDescription>
              We couldn&apos;t verify your address with USPS. You can continue with the address you
              entered.
            </DialogDescription>
          </DialogHeader>

          <Alert variant="default" className="bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              {error.message || 'Address verification was not successful.'}
            </AlertDescription>
          </Alert>

          <AddressDisplay label="Your Address" address={originalAddress} variant="original" />

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Edit Address
            </Button>
            <Button onClick={onKeepOriginal}>Continue Anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // If there are corrections/suggestions
  if (suggestedAddress && hasCorrections) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              Is this address correct?
            </DialogTitle>
            <DialogDescription>
              USPS suggests a standardized version of your address. Would you like to use the
              suggested address?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <AddressDisplay
              label="USPS Suggested Address"
              address={suggestedAddress}
              variant="suggested"
            />

            <AddressDisplay
              label="Address You Entered"
              address={originalAddress}
              variant="original"
            />
          </div>

          {verified && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                The suggested address has been verified as deliverable by USPS.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={onKeepOriginal}>
              Use Original
            </Button>
            <Button onClick={() => onAcceptSuggested(suggestedAddress)}>Use Suggested</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Default case - address verified without corrections (shouldn't normally reach here)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Address Verification</DialogTitle>
        </DialogHeader>

        <AddressDisplay label="Your Address" address={originalAddress} />

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Continue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
